import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ordersService } from "api/services/orders.service";
import ContentHeader from "components/Layout/ContentHeader";
import ProductOrderCancelModal from "components/order-management/ProductOrderCancelModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";

import type { AdminOrder } from "api/types/order.types";
import "pages/AdminPage.scss";

// 투어 주문 = 기존 어드민 주문 목록 API 를 product_type=TRAVEL 로 필터해 재사용.
// (백엔드 base queryset 이 TRAVEL 을 허용해야 데이터가 내려옴 — docs/투어주문목록_API_요청서.md 참고)
const PRODUCT_TYPE = "TRAVEL";

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: "결제완료",
  PENDING: "결제대기",
  PAYMENT_ATTEMPTED: "결제시도",
  PAYMENT_PROCESSING: "결제처리중",
  PAYMENT_FAILED: "결제실패",
  CANCELLED: "취소됨",
  COMPLETED: "완료",
};
const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "info" | "draft"> = {
  CONFIRMED: "active",
  PENDING: "pending",
  PAYMENT_ATTEMPTED: "pending",
  PAYMENT_PROCESSING: "info",
  PAYMENT_FAILED: "rejected",
  CANCELLED: "rejected",
  COMPLETED: "active",
};

const PAGE_SIZE = 10;

const TravelOrderManagement: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchOrders = async (page: number, status: string, q: string, from: string, to: string) => {
    setLoading(true);
    try {
      const res = await ordersService.list({
        page,
        page_size: PAGE_SIZE,
        product_type: PRODUCT_TYPE,
        ...(status ? { status: status as AdminOrder["status"] } : {}),
        ...(q ? { search: q } : {}),
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
      });
      setOrders(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, statusFilter, search, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders(1, statusFilter, search, dateFrom, dateTo);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await ordersService.exportExcel({
        product_type: PRODUCT_TYPE,
        ...(statusFilter ? { status: statusFilter as AdminOrder["status"] } : {}),
        ...(search ? { search } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `travel_orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const fallback = "엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.";
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      let message = fallback;
      try {
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed?.error ?? parsed?.detail ?? parsed?.message ?? fallback;
        } else if (data && typeof data === "object") {
          const d = data as { error?: string; detail?: string; message?: string };
          message = d.error ?? d.detail ?? d.message ?? fallback;
        }
      } catch {
        message = fallback;
      }
      alert(message);
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<AdminOrder>[] = [
    { key: "order_number", title: "주문번호" },
    {
      key: "user_name",
      title: "고객명",
      render: (_, row) => String(row.user?.name ?? "-"),
    },
    {
      key: "product_info",
      title: "투어명",
      render: (_, row) => String(row.product_info?.name ?? "-"),
    },
    {
      key: "quantity",
      title: "인원",
      width: "70px",
      render: (v) => `${Number(v ?? 0)}명`,
    },
    {
      key: "total_amount",
      title: "결제금액",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    { key: "created_at", title: "주문일", width: "120px", render: (v) => String(v).slice(0, 10) },
    {
      key: "status",
      title: "결제상태",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{STATUS_LABEL[v]}</Badge>,
    },
    {
      key: "actions",
      title: "",
      render: (_, row) => {
        if (!row.can_cancel || row.status === "CANCELLED") return null;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOrder(row);
              setShowCancelModal(true);
            }}
          >
            주문 취소
          </Button>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="투어 주문 관리"
        description="투어 상품의 모든 주문 내역을 조회합니다."
        actions={[
          <Button key="export" variant="outline" onClick={handleExportExcel} disabled={exporting}>
            {exporting ? "다운로드 중..." : "엑셀 다운로드"}
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="주문번호, 고객명 검색" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ width: 260 }} prefix="🔍" />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: "", label: "결제상태 전체" },
              { value: "CONFIRMED", label: "결제완료" },
              { value: "PENDING", label: "결제대기" },
              { value: "PAYMENT_FAILED", label: "결제실패" },
              { value: "CANCELLED", label: "취소됨" },
              { value: "COMPLETED", label: "완료" },
            ]}
            style={{ width: 140 }}
          />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <span style={{ color: "#71717A" }}>~</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <Button variant="secondary" size="sm" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={orders} rowKey="id" onRowClick={(row) => navigate(`/order-management/${row.id}`)} />}
        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={setCurrentPage} />
      </div>

      <ProductOrderCancelModal open={showCancelModal} order={selectedOrder} onClose={() => setShowCancelModal(false)} onCancelled={() => fetchOrders(currentPage, statusFilter, search, dateFrom, dateTo)} />
    </div>
  );
};

export default TravelOrderManagement;
